/**
 * /calendar — your week, with everything on it.
 *
 * Layout: 7 day-columns × an hour grid. On each column we draw:
 *   - The user's working-hours band as a subtle background.
 *   - Busy events from the linked calendars in muted grey.
 *   - Personal tasks coloured by work_level — deep is the accent
 *     clove, shallow a calm leaf, admin a soft mist.
 *   - Proposals as dashed-outline cards so they read as "pending"
 *     versus the solid "scheduled" cards.
 *
 * Interactions:
 *   - Click an empty area → "Add task here" inline form, pre-fills
 *     start time + duration.
 *   - Click an event → opens the edit drawer (when it's a task).
 *   - ‹ › arrows / "today" navigate by week.
 *
 * Phase-2 / nice-to-haves we deliberately punted on:
 *   - DnD reschedule (we have a /reschedule endpoint; click-and-pick
 *     covers the proposal flow without the extra DnD surface).
 *   - Month view / day view toggles.
 */

import { useEffect, useMemo, useState } from 'react';
import { api, type PersonalTask } from '../api';
import { Link } from '../router';
import { LoadingBlock } from '../components/Spinner';
import { Modal } from '../components/Modal';
import { useToast } from '../components/Toast';
import { UiNode } from '../lib/UiNode';
import { IconClock, IconPlus, IconCalendar, IconShare } from '../components/Icon';
import { TaskInbox } from '../components/TaskInbox';

const HOUR_PX = 56;
const DAY_START_HOUR = 6;
const DAY_END_HOUR = 22;
const TOTAL_HOURS = DAY_END_HOUR - DAY_START_HOUR;
const COL_PADDING = 4;

interface BusyEvent {
  startsAt: number;
  endsAt: number;
  title: string | null;
  source: 'calendar';
}

interface CalendarState {
  busy: BusyEvent[];
  tasks: PersonalTask[];
  workingHours: Array<{ dayOfWeek: number; startMinute: number; endMinute: number }>;
  timezone: string;
}

export function CalendarScreen() {
  const [weekStart, setWeekStart] = useState<Date>(() => startOfWeek(new Date()));
  const [state, setState] = useState<CalendarState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<PersonalTask | null>(null);
  const [creating, setCreating] = useState<{ defaultStart: Date } | null>(null);
  const { toast } = useToast();

  async function refresh(): Promise<void> {
    try {
      // Window: a week before and after the visible week, so events
      // spanning the edges don't disappear when navigating.
      const winFrom = Math.floor(weekStart.getTime() / 1000) - 7 * 86400;
      const winTo = winFrom + 21 * 86400;
      const [tasksRes, whRes, eventsRes] = await Promise.all([
        api.listPersonalTasks(),
        api.getWorkingHours(),
        api.listCalendarEvents(winFrom, winTo),
      ]);
      setState({
        busy: eventsRes.events
          .filter((e) => e.busy && !e.allDay)
          .map((e) => ({
            startsAt: e.startsAt,
            endsAt: e.endsAt,
            title: e.title,
            source: 'calendar' as const,
          })),
        tasks: tasksRes.tasks,
        workingHours: whRes.days,
        timezone: whRes.timezone,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    }
  }

  useEffect(() => {
    void refresh();
    const onChange = () => void refresh();
    window.addEventListener('synfolio:tasks-changed', onChange);
    return () => window.removeEventListener('synfolio:tasks-changed', onChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStart]);

  const days = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      return d;
    });
  }, [weekStart]);

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-[22px] font-medium tracking-tight">
            <IconCalendar size={20} />
            Calendar
          </h1>
          <p className="mt-1 text-sm text-cream-3">
            Your busy time, your working hours, your tasks — all in one view.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <UiNode
            as="button"
            name="calendar-prev-week"
            entity="calendar"
            action="prev_week"
            type="button"
            className="btn btn-ghost text-xs"
            onClick={() => setWeekStart(addDays(weekStart, -7))}
          >
            ‹ Previous
          </UiNode>
          <UiNode
            as="button"
            name="calendar-today"
            entity="calendar"
            action="today"
            type="button"
            className="btn btn-ghost text-xs"
            onClick={() => setWeekStart(startOfWeek(new Date()))}
          >
            This week
          </UiNode>
          <UiNode
            as="button"
            name="calendar-next-week"
            entity="calendar"
            action="next_week"
            type="button"
            className="btn btn-ghost text-xs"
            onClick={() => setWeekStart(addDays(weekStart, 7))}
          >
            Next ›
          </UiNode>
          <UiNode
            as="button"
            name="calendar-add-task"
            entity="task"
            action="open_create"
            type="button"
            className="btn btn-accent inline-flex items-center gap-1.5 text-xs"
            onClick={() =>
              setCreating({
                defaultStart: roundUpQuarter(new Date()),
              })
            }
          >
            <IconPlus size={14} /> New task
          </UiNode>
          <Link
            to="/profile?focus=shares"
            className="btn btn-ghost inline-flex items-center gap-1.5 text-xs"
          >
            <IconShare size={12} /> Share
          </Link>
          <Link to="/profile" className="btn btn-ghost text-xs">
            Settings
          </Link>
        </div>
      </header>

      <p className="text-[11px] text-cream-3">
        Week of{' '}
        <span className="text-cream-2">
          {weekStart.toLocaleDateString(undefined, {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
          })}
        </span>
      </p>

      {error && (
        <div className="panel border-rust/40 p-3 text-sm text-rust">{error}</div>
      )}
      {!state ? (
        <LoadingBlock label="Loading your week…" />
      ) : (
        <>
          <TaskInbox onChanged={() => void refresh()} />
          <WeekGrid
            days={days}
            state={state}
            onTaskClick={setEditing}
            onEmptyClick={(date) => setCreating({ defaultStart: date })}
            onTaskDrop={async (taskId, newStartUnix) => {
              const task = state.tasks.find((t) => t.id === taskId);
              if (!task || !task.scheduledStartsAt || !task.scheduledEndsAt) return;
              const duration = task.scheduledEndsAt - task.scheduledStartsAt;
              // Optimistic update: paint the new position before the
              // round-trip so the drop feels instant.
              setState((cur) =>
                cur
                  ? {
                      ...cur,
                      tasks: cur.tasks.map((t) =>
                        t.id === taskId
                          ? {
                              ...t,
                              scheduledStartsAt: newStartUnix,
                              scheduledEndsAt: newStartUnix + duration,
                            }
                          : t,
                      ),
                    }
                  : cur,
              );
              try {
                await api.patchPersonalTask(taskId, {
                  scheduledStartsAt: newStartUnix,
                  scheduledEndsAt: newStartUnix + duration,
                  // Proposed tasks become "scheduled" once the user
                  // physically picks where they go.
                  ...(task.status === 'proposed' ? { status: 'scheduled' as const } : {}),
                });
                toast({ tone: 'success', title: 'Moved' });
              } catch {
                toast({ tone: 'warning', title: 'Move failed' });
                void refresh();
              }
            }}
          />
        </>
      )}

      {editing && (
        <TaskEditModal
          task={editing}
          onClose={() => setEditing(null)}
          onChanged={() => {
            setEditing(null);
            void refresh();
          }}
        />
      )}
      {creating && (
        <TaskCreateModal
          defaultStart={creating.defaultStart}
          onClose={() => setCreating(null)}
          onCreated={(result) => {
            setCreating(null);
            void refresh();
            if (result.scheduled) {
              if (result.mode === 'always') {
                toast({
                  tone: 'success',
                  title: 'Scheduled',
                  body: `${result.task.title} is on your calendar.`,
                });
              } else {
                // Proposed — ProposalWatcher will pick it up via the
                // synfolio:proposal-changed event below.
                window.dispatchEvent(new Event('synfolio:proposal-changed'));
              }
            } else if (result.reason === 'no-working-hours') {
              toast({
                tone: 'warning',
                title: 'Saved to inbox',
                body: 'Set your working hours in Profile so we can auto-schedule.',
              });
            } else if (result.reason === 'no-room') {
              toast({
                tone: 'warning',
                title: 'Saved to inbox',
                body: 'No slot fits in the next 30 days — try shrinking the task or widening your hours.',
              });
            } else {
              toast({ title: 'Saved to inbox' });
            }
          }}
        />
      )}
    </div>
  );
}

// ---- Week grid ----------------------------------------------------------

function WeekGrid({
  days,
  state,
  onTaskClick,
  onEmptyClick,
  onTaskDrop,
}: {
  days: Date[];
  state: CalendarState;
  onTaskClick: (t: PersonalTask) => void;
  onEmptyClick: (date: Date) => void;
  /** Fired when a task block is dropped on a different day-column /
   *  hour. newStartUnix is snapped to 15-minute increments. */
  onTaskDrop: (taskId: string, newStartUnix: number) => void;
}) {
  const todayKey = isoLocalDate(new Date());
  return (
    <div className="panel overflow-x-auto p-0">
      <div
        className="grid min-w-[720px]"
        style={{ gridTemplateColumns: `48px repeat(7, minmax(0, 1fr))` }}
      >
        {/* Header row */}
        <div className="sticky top-0 z-10 border-b border-ink-rim bg-ink-2/95 backdrop-blur"></div>
        {days.map((d) => {
          const isToday = isoLocalDate(d) === todayKey;
          return (
            <div
              key={d.toISOString()}
              className="sticky top-0 z-10 border-b border-ink-rim bg-ink-2/95 px-2 py-2 backdrop-blur"
            >
              <div className="flex items-baseline justify-between">
                <span
                  className={`text-xs font-medium ${isToday ? 'text-clove-2' : 'text-cream-2'}`}
                >
                  {d.toLocaleDateString(undefined, { weekday: 'short' })}
                </span>
                <span
                  className={`text-base font-semibold ${isToday ? 'text-clove' : 'text-cream'}`}
                >
                  {d.getDate()}
                </span>
              </div>
            </div>
          );
        })}

        {/* Hour-label column */}
        <div className="relative" style={{ height: HOUR_PX * TOTAL_HOURS }}>
          {Array.from({ length: TOTAL_HOURS + 1 }, (_, i) => {
            const h = DAY_START_HOUR + i;
            return (
              <div
                key={i}
                className="absolute right-1 -translate-y-1.5 text-[10px] text-cream-3"
                style={{ top: i * HOUR_PX }}
              >
                {formatHour(h)}
              </div>
            );
          })}
        </div>

        {/* Day columns */}
        {days.map((d, dayIndex) => {
          const dayKey = isoLocalDate(d);
          const dayStart = startOfLocalDay(d).getTime() / 1000;
          const dayEnd = dayStart + 86400;
          const dow = d.getDay();
          const wh = state.workingHours.find((w) => w.dayOfWeek === dow);
          // Compose blocks for this day.
          const dayBusy = state.busy.filter(
            (b) => b.endsAt > dayStart && b.startsAt < dayEnd,
          );
          const dayTasks = state.tasks.filter(
            (t) =>
              t.scheduledStartsAt &&
              t.scheduledEndsAt &&
              t.scheduledEndsAt > dayStart &&
              t.scheduledStartsAt < dayEnd,
          );
          const isToday = dayKey === todayKey;
          return (
            <div
              key={dayIndex}
              className="relative border-l border-ink-rim/60"
              style={{ height: HOUR_PX * TOTAL_HOURS }}
              onClick={(e) => {
                // Only fire when the click is on the empty grid, not
                // on a bubbled child (event card).
                if (e.target !== e.currentTarget) return;
                const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
                const y = e.clientY - rect.top;
                const minutesFromStart = Math.floor(y / HOUR_PX) * 60;
                const startDate = startOfLocalDay(d);
                startDate.setMinutes(
                  startDate.getMinutes() + DAY_START_HOUR * 60 + minutesFromStart,
                );
                onEmptyClick(startDate);
              }}
              onDragOver={(e) => {
                // Allow drop on this column.
                if (e.dataTransfer.types.includes('application/x-synfolio-task')) {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                }
              }}
              onDrop={(e) => {
                const taskId = e.dataTransfer.getData('application/x-synfolio-task');
                if (!taskId) return;
                e.preventDefault();
                const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
                const y = e.clientY - rect.top;
                // Convert y → minutes from DAY_START_HOUR, snapped
                // to 15-minute increments so drops feel decisive.
                const minutesFromGridTop = Math.max(0, Math.round((y / HOUR_PX) * 4) * 15);
                const totalMinuteOfDay = DAY_START_HOUR * 60 + minutesFromGridTop;
                const newStart = dayStart + totalMinuteOfDay * 60;
                onTaskDrop(taskId, newStart);
              }}
            >
              {/* Hour grid lines */}
              {Array.from({ length: TOTAL_HOURS }, (_, i) => (
                <div
                  key={i}
                  className="pointer-events-none absolute inset-x-0 border-t border-ink-rim/30"
                  style={{ top: i * HOUR_PX }}
                />
              ))}

              {/* Today's background hint */}
              {isToday && (
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-0"
                  style={{
                    background:
                      'linear-gradient(180deg, color-mix(in oklab, var(--color-clove) 4%, transparent), transparent 35%)',
                  }}
                />
              )}

              {/* Working hours band */}
              {wh && (
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-x-0"
                  style={{
                    top: minuteToY(wh.startMinute),
                    height: minuteToY(wh.endMinute) - minuteToY(wh.startMinute),
                    background:
                      'linear-gradient(180deg, color-mix(in oklab, var(--color-leaf) 3.5%, transparent), color-mix(in oklab, var(--color-leaf) 3.5%, transparent))',
                    borderLeft:
                      '2px solid color-mix(in oklab, var(--color-leaf) 30%, transparent)',
                  }}
                />
              )}

              {/* Now line */}
              {isToday && <NowLine />}

              {/* Busy events (calendar) */}
              {dayBusy.map((b, i) => (
                <BusyBlock key={`b-${i}`} event={b} dayStart={dayStart} />
              ))}

              {/* Personal tasks */}
              {dayTasks.map((t) => (
                <TaskBlock
                  key={t.id}
                  task={t}
                  dayStart={dayStart}
                  onClick={() => onTaskClick(t)}
                />
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function NowLine() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);
  const minutes = now.getHours() * 60 + now.getMinutes();
  if (minutes < DAY_START_HOUR * 60 || minutes > DAY_END_HOUR * 60) return null;
  const y = minuteToY(minutes);
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-x-0 z-20"
      style={{ top: y }}
    >
      <div className="h-px bg-clove/80" />
      <div
        className="absolute -left-1 -top-1 h-2 w-2 rounded-full"
        style={{ background: 'var(--color-clove)' }}
      />
    </div>
  );
}

function BusyBlock({ event, dayStart }: { event: BusyEvent; dayStart: number }) {
  const startMin = clampMin(((event.startsAt - dayStart) / 60));
  const endMin = clampMin(((event.endsAt - dayStart) / 60));
  if (endMin <= DAY_START_HOUR * 60 || startMin >= DAY_END_HOUR * 60) return null;
  return (
    <div
      className="absolute inset-x-1 overflow-hidden rounded-md text-[10px] leading-tight"
      style={{
        top: minuteToY(startMin) + COL_PADDING,
        height: Math.max(20, minuteToY(endMin) - minuteToY(startMin) - 2 * COL_PADDING),
        background:
          'repeating-linear-gradient(135deg, color-mix(in oklab, var(--color-ash) 22%, transparent) 0 4px, transparent 4px 8px)',
        border: '1px solid color-mix(in oklab, var(--color-ash) 30%, transparent)',
        color: 'var(--color-cream-3)',
      }}
      title={event.title ?? 'Busy'}
    >
      <div className="truncate px-1.5 py-0.5">{event.title ?? 'Busy'}</div>
    </div>
  );
}

function TaskBlock({
  task,
  dayStart,
  onClick,
}: {
  task: PersonalTask;
  dayStart: number;
  onClick: () => void;
}) {
  if (!task.scheduledStartsAt || !task.scheduledEndsAt) return null;
  const startMin = clampMin((task.scheduledStartsAt - dayStart) / 60);
  const endMin = clampMin((task.scheduledEndsAt - dayStart) / 60);
  if (endMin <= DAY_START_HOUR * 60 || startMin >= DAY_END_HOUR * 60) return null;

  const tones = {
    deep: {
      bg: 'color-mix(in oklab, var(--color-clove) 22%, var(--color-ink-2))',
      border: 'color-mix(in oklab, var(--color-clove) 60%, transparent)',
      label: 'var(--color-clove-2)',
    },
    shallow: {
      bg: 'color-mix(in oklab, var(--color-leaf) 18%, var(--color-ink-2))',
      border: 'color-mix(in oklab, var(--color-leaf) 50%, transparent)',
      label: 'var(--color-leaf)',
    },
    admin: {
      bg: 'color-mix(in oklab, var(--color-mist) 18%, var(--color-ink-2))',
      border: 'color-mix(in oklab, var(--color-mist) 50%, transparent)',
      label: 'var(--color-mist)',
    },
  } as const;
  const tone = tones[task.workLevel];
  const proposed = task.status === 'proposed';
  return (
    <UiNode
      as="button"
      name="calendar-task-block"
      entity="task"
      entityId={task.id}
      action="open_edit"
      state={task.status}
      type="button"
      draggable
      onDragStart={(e: React.DragEvent) => {
        // Use a SynFolio-specific MIME type so other drag sources
        // (text selection, file drops) don't accidentally trigger
        // the drop handler. Setting effectAllowed gives the OS the
        // right cursor.
        e.dataTransfer.setData('application/x-synfolio-task', task.id);
        e.dataTransfer.effectAllowed = 'move';
      }}
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation();
        onClick();
      }}
      className="absolute inset-x-1 cursor-grab overflow-hidden rounded-md p-1.5 text-left text-[11px] leading-tight transition hover:brightness-110 active:cursor-grabbing"
      style={{
        top: minuteToY(startMin) + COL_PADDING,
        height: Math.max(20, minuteToY(endMin) - minuteToY(startMin) - 2 * COL_PADDING),
        background: tone.bg,
        border: `1px ${proposed ? 'dashed' : 'solid'} ${tone.border}`,
        color: 'var(--color-cream)',
      }}
      title={task.title}
    >
      <div className="flex items-center gap-1 text-[9px] font-medium uppercase tracking-wide" style={{ color: tone.label }}>
        {task.workLevel}
        {proposed && <span className="text-cream-4">· proposed</span>}
      </div>
      <div className="truncate font-medium">{task.title}</div>
    </UiNode>
  );
}

// ---- Create / Edit modals -----------------------------------------------

function TaskCreateModal({
  defaultStart,
  onClose,
  onCreated,
}: {
  defaultStart: Date;
  onClose: () => void;
  onCreated: (r: {
    task: PersonalTask;
    scheduled: boolean;
    mode?: 'always' | 'ask' | 'never';
    reason?: string;
  }) => void;
}) {
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [workLevel, setWorkLevel] = useState<'deep' | 'shallow' | 'admin'>('shallow');
  const [estimatedMinutes, setEstimatedMinutes] = useState(30);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // defaultStart is currently only used to inform copy — the
  // scheduler always picks the next free slot; pinning to a
  // user-clicked moment is in the next iteration.
  void defaultStart;

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!title.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.createPersonalTask({
        title: title.trim(),
        notes: notes.trim() || undefined,
        workLevel,
        estimatedMinutes,
      });
      onCreated(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open onClose={onClose} title="New task" maxWidth={480}>
      <form className="flex flex-col gap-4" onSubmit={submit}>
        <label className="flex flex-col gap-1.5 text-xs text-cream-3">
          Title
          <input
            className="input text-sm"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            autoFocus
            placeholder="Write the brief"
            maxLength={200}
            required
          />
        </label>
        <label className="flex flex-col gap-1.5 text-xs text-cream-3">
          Notes
          <textarea
            className="input min-h-[64px] text-sm"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional — context, links, success criteria…"
            maxLength={2000}
          />
        </label>
        <fieldset className="flex flex-col gap-1.5 text-xs text-cream-3">
          <legend className="text-xs text-cream-3">Work level</legend>
          <div className="grid grid-cols-3 gap-1.5">
            {(
              [
                { id: 'deep', label: 'Deep', hint: '90+ min, focus' },
                { id: 'shallow', label: 'Shallow', hint: '30+ min' },
                { id: 'admin', label: 'Admin', hint: '15 min, quick' },
              ] as const
            ).map((lv) => (
              <button
                key={lv.id}
                type="button"
                onClick={() => {
                  setWorkLevel(lv.id);
                  // Suggest the default duration for that level.
                  setEstimatedMinutes(lv.id === 'deep' ? 90 : lv.id === 'shallow' ? 30 : 15);
                }}
                className={`flex flex-col items-start gap-0.5 rounded-lg border p-2 text-left text-sm transition ${
                  workLevel === lv.id
                    ? 'border-clove bg-clove/10 text-cream'
                    : 'border-ink-rim text-cream-2 hover:bg-ink-3'
                }`}
              >
                <span className="font-medium">{lv.label}</span>
                <span className="text-[10px] text-cream-3">{lv.hint}</span>
              </button>
            ))}
          </div>
        </fieldset>
        <label className="flex flex-col gap-1.5 text-xs text-cream-3">
          Estimate · <span className="text-cream-2">{estimatedMinutes} min</span>
          <input
            type="range"
            min={5}
            max={240}
            step={5}
            value={estimatedMinutes}
            onChange={(e) => setEstimatedMinutes(Number(e.target.value))}
          />
        </label>
        {error && <p className="text-xs text-rust">{error}</p>}
        <div className="flex items-center justify-end gap-2">
          <button type="button" className="btn btn-ghost text-xs" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-accent text-xs"
            disabled={!title.trim() || busy}
          >
            {busy ? 'Scheduling…' : 'Create'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function TaskEditModal({
  task,
  onClose,
  onChanged,
}: {
  task: PersonalTask;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [title, setTitle] = useState(task.title);
  const [notes, setNotes] = useState(task.notes ?? '');
  const [estimatedMinutes, setEstimatedMinutes] = useState(task.estimatedMinutes);
  const [busy, setBusy] = useState(false);

  async function save(): Promise<void> {
    setBusy(true);
    try {
      await api.patchPersonalTask(task.id, {
        title: title.trim(),
        notes: notes.trim() || null,
        estimatedMinutes,
      });
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  async function markDone(): Promise<void> {
    setBusy(true);
    try {
      await api.patchPersonalTask(task.id, { status: 'done' });
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  async function remove(): Promise<void> {
    if (!confirm(`Delete "${task.title}"?`)) return;
    setBusy(true);
    try {
      await api.deletePersonalTask(task.id);
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  async function reschedule(): Promise<void> {
    setBusy(true);
    try {
      await api.reschedulePersonalTask(task.id);
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open onClose={onClose} title={task.title} maxWidth={480}>
      <div className="flex flex-col gap-4">
        {task.scheduledStartsAt && task.scheduledEndsAt && (
          <div className="flex items-center gap-2 text-xs text-cream-3">
            <IconClock size={12} />
            <span>
              {new Date(task.scheduledStartsAt * 1000).toLocaleString(undefined, {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
              })}{' '}
              –{' '}
              {new Date(task.scheduledEndsAt * 1000).toLocaleTimeString(undefined, {
                hour: 'numeric',
                minute: '2-digit',
              })}
            </span>
            {task.status === 'proposed' && (
              <span className="chip chip-accent text-[10px]">Proposed</span>
            )}
          </div>
        )}
        <label className="flex flex-col gap-1.5 text-xs text-cream-3">
          Title
          <input
            className="input text-sm"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </label>
        <label className="flex flex-col gap-1.5 text-xs text-cream-3">
          Notes
          <textarea
            className="input min-h-[64px] text-sm"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </label>
        <label className="flex flex-col gap-1.5 text-xs text-cream-3">
          Estimate · <span className="text-cream-2">{estimatedMinutes} min</span>
          <input
            type="range"
            min={5}
            max={240}
            step={5}
            value={estimatedMinutes}
            onChange={(e) => setEstimatedMinutes(Number(e.target.value))}
          />
        </label>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-1.5">
            {task.status !== 'done' && (
              <button
                type="button"
                className="btn btn-ghost text-xs"
                onClick={markDone}
                disabled={busy}
              >
                Mark done
              </button>
            )}
            {(task.status === 'scheduled' || task.status === 'proposed') && (
              <button
                type="button"
                className="btn btn-ghost text-xs"
                onClick={reschedule}
                disabled={busy}
              >
                Pick another slot
              </button>
            )}
            <button
              type="button"
              className="btn btn-ghost text-xs text-rust"
              onClick={remove}
              disabled={busy}
            >
              Delete
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" className="btn btn-ghost text-xs" onClick={onClose}>
              Close
            </button>
            <button
              type="button"
              className="btn btn-primary text-xs"
              onClick={save}
              disabled={busy}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ---- helpers ------------------------------------------------------------

function startOfWeek(d: Date): Date {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  // Monday-start week — feels right for "the work week" framing.
  const day = out.getDay(); // 0=Sun, 1=Mon, …
  const diff = day === 0 ? -6 : 1 - day;
  out.setDate(out.getDate() + diff);
  return out;
}

function startOfLocalDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(d.getDate() + n);
  return x;
}

function isoLocalDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function minuteToY(minute: number): number {
  return ((minute - DAY_START_HOUR * 60) / 60) * HOUR_PX;
}

function clampMin(m: number): number {
  return Math.max(0, Math.min(24 * 60, m));
}

function formatHour(h: number): string {
  if (h === 0 || h === 24) return '12a';
  if (h === 12) return '12p';
  return h < 12 ? `${h}a` : `${h - 12}p`;
}

function roundUpQuarter(d: Date): Date {
  const x = new Date(d);
  const m = x.getMinutes();
  x.setMinutes(Math.ceil(m / 15) * 15, 0, 0);
  return x;
}
